import React, { useState, useEffect, useRef } from 'react';
import { Modal, Radio, Button, Typography, Result, Tabs } from 'antd';
import { AlertCircle, CheckCircle, Smartphone, Layers, RefreshCw } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { brokerSyncAction, brokerResetAndSyncAction, getSyncStatusAction, getjwtAction } from '../../redux/actions/brokerAction';
import './SyncBrokerModal.css';

const steps = [
  { key: 'orders', label: 'Orders', icon: <Layers size={16} /> },
  { key: 'positions', label: 'Positions', icon: <Smartphone size={16} /> },
  { key: 'trades', label: 'Trades', icon: <RefreshCw size={16} /> },
];

const SyncBrokerModal = ({ show, onHide, brokerId, onSyncSuccess, syncStatus }) => {
  const [syncType, setSyncType] = useState(2);
  const [loading, setLoading] = useState(false);
  const [viewState, setViewState] = useState('initial');
  const [progressState, setProgressState] = useState({
    phase: '',
    step: '',
    summary: null,
    error: null
  });
  
  const dispatch = useDispatch();
  const socketJWT = useSelector((state) => state.socketJwt.data);

  useEffect(() => {
    if (show && brokerId) {
      checkSyncStatus();
    }
  }, [show, brokerId]);

  useEffect(() => {
    if (show && syncStatus) {
        handleSyncUpdate(syncStatus);
    }
  }, [show, syncStatus]);

  const handleSyncUpdate = (data) => {
    
    if (data.phase === 'in_progress') {
        setProgressState(prev => ({ ...prev, phase: data.phase, step: data.step }));
        setViewState('progressing');
    } else if (data.phase === 'completed') {
        setProgressState(prev => ({ 
            ...prev, 
            phase: data.phase, 
            summary: data.summary 
        }));
        setViewState('summary');
    } else if (data.phase === 'failed') {
        setProgressState(prev => ({ 
            ...prev, 
            phase: data.phase, 
            error: data.summary?.errors 
        }));
        setViewState('error');
    }
  };

  const checkSyncStatus = async () => {
    const res = await dispatch(getSyncStatusAction(brokerId));
    if (res?.status && res.data) {
        const { phase, step, summary } = res.data;
        if (phase === 'in_progress' || phase === 'requested') {
            setViewState('progressing');
            setProgressState({ phase, step, summary: null, error: null });
        }
    }
  };

  const handleSync = async () => {
    setViewState('progressing');
    setProgressState({ phase: 'requested', step: '', summary: null, error: null });
    try {
      if (syncType === 1) {
        await dispatch(brokerResetAndSyncAction(brokerId));
      } else {
        await dispatch(brokerSyncAction(brokerId));
      }
    } catch (error) {
      console.error("Sync error:", error);
    }
  };

  const handleClose = () => {
    setViewState('initial');
    setProgressState({ phase: '', step: '', summary: null, error: null });
    if(viewState === 'summary') {
        if (onSyncSuccess) onSyncSuccess();
    }
    onHide();
  };

  const renderInitial = () => (
    <div className="sync-modal-body">
        <div className="sync-options-wrapper">
          <div 
            onClick={() => setSyncType(1)} 
            className={`sync-option-card ${syncType === 1 ? 'selected' : ''}`}
          >
            <Radio checked={syncType === 1} className="sync-option-radio" />
            <div className="sync-option-content">
              <div className="sync-option-header">
                Reset & Sync Broker Positions
              </div>
              <div className="sync-option-description">
                All existing QuikTrade positions (Terminal & Algo positions for the selected broker account) will be cleared and current broker positions will be re-synced from scratch. This removes all open and closed positions, including SL, targets, trailing rules, and strategy baskets.
              </div>
              <div className="sync-reset-alert">
                <AlertCircle size={14} /> Use only if you want a complete reset. This action cannot be undone.
              </div>
            </div>
          </div>

          <div 
            onClick={() => setSyncType(2)} 
            className={`sync-option-card ${syncType === 2 ? 'selected' : ''}`}
          >
            <Radio checked={syncType === 2} className="sync-option-radio" />
            <div className="sync-option-content">
              <div className="sync-option-header">
                Sync Broker Positions
              </div>
              <div className="sync-option-description no-margin">
                Any new or missing broker positions will be synced into QuikTrade. Adjustment positions will be added as single-leg positions, without modifying existing QuikTrade setups.
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  const renderProgress = () => {
    const activeStepIndex = steps.findIndex(s => s.key === progressState.step);
    
    return (
        <div className="sync-progress-wrapper">
             <p className="sync-phase-title">
                {progressState.phase === 'requested' ? 'Initiating Sync...' : 'Syncing in Progress...'}
             </p>
             
             <div className="sync-steps-container">
                {steps.map((s, index) => {
                    let statusClass = '';
                    if (progressState.phase === 'completed') statusClass = 'completed';
                    else if (activeStepIndex > index) statusClass = 'completed';
                    else if (activeStepIndex === index) statusClass = 'active';
                    
                    return (
                        <div key={s.key} className={`sync-step ${statusClass}`}>
                            <div className="sync-step-icon">
                                {statusClass === 'completed' ? <CheckCircle size={16} /> : s.icon}
                            </div>
                            <div className="sync-step-label">{s.label}</div>
                        </div>
                    );
                })}
             </div>
             
             <div style={{ marginTop: 20, color: '#666' }}>
                Please wait while we fetch your data from the broker.
             </div>
        </div>
    );
  };

  const renderSummary = () => {
      const { summary } = progressState;
      if (!summary) return null;

      const tabsItems = [
          {
              key: 'orders',
              label: `Orders`,
              children: renderSummaryTable(summary.orders?.added || [], 'Order')
          },
          {
              key: 'positions',
              label: `Positions`,
              children: renderSummaryTable(summary.positions?.added || [], 'Position')
          },
          {
              key: 'trades',
              label: `Trades`,
              children: renderSummaryTable(summary.trades?.added || [], 'Trade')
          }
      ];

      return (
          <div className="sync-summary-wrapper">
              <div className="sync-summary-header">
                  <div className="sync-summary-title">
                      <CheckCircle className="sync-success-icon" size={20} />
                      Sync Completed Successfully
                  </div>
              </div>
              <div style={{ padding: '0 20px' }}>
                <Tabs defaultActiveKey="orders" items={tabsItems} />
              </div>
          </div>
      );
  };

  const renderSummaryTable = (data, type) => {
      if (!data || data.length === 0) {
          return <div className="empty-state">No {type}s added or modified.</div>;
      }
      return (
        <div className="sync-table-container">
            <table className="sync-table">
                <thead>
                    <tr>
                        <th>Symbol</th>
                        {type !== 'Position' && <th>Type</th>}
                        {(type === 'Position' || type === 'Trade') && <th>Product</th>}
                        <th>Qty</th>
                        <th>Price</th>
                        {type === 'Order' && <th>Order Type</th>}
                        {type === 'Order' && <th>Order ID</th>}
                        {type === 'Trade' && <th>Trade ID</th>}
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, idx) => (
                        <tr key={idx}>
                            <td>{item.tradingSymbol}</td>
                            {type !== 'Position' && (
                                <td>
                                    <span className={`txn-type-${item.transactionType}`}>
                                        {item.transactionType}
                                    </span>
                                </td>
                            )}
                            {(type === 'Position' || type === 'Trade') && <td>{item.product}</td>}
                            <td>{type === 'Position' ? item?.netQuantity : (item?.quantity || item?.netQuantity)}</td>
                            <td>{item.averagePrice || item.price || 0}</td>
                            {type === 'Order' && <td>{item.orderType}</td>}
                            {type === 'Order' && <td>{item.orderId}</td>}
                            {type === 'Trade' && <td>{item.tradeId}</td>}
                            <td>
                                <span className="sync-badge added">Added</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      )
  };

  const getFooter = () => {
    if (viewState === 'initial') {
        return [
            <Button key="cancel" onClick={handleClose} className="sync-modal-btn">Cancel</Button>,
            <Button key="submit" type="primary" loading={loading} onClick={handleSync} className="sync-modal-btn sync-modal-submit">Submit</Button>
        ];
    } else if (viewState === 'progressing') {
        return []; // No actions while syncing
    } else {
        return [
            <Button key="done" type="primary" onClick={handleClose} className="sync-modal-btn sync-modal-submit">Done</Button>
        ];
    }
  };

  return (
    <Modal
      title={viewState === 'initial' ? "Sync Broker Positions" : (viewState === 'summary' ? "Sync Summary" : "Sync Status")}
      open={show}
      onCancel={viewState === 'progressing' ? null : handleClose}
      footer={getFooter()}
      width={viewState === 'summary' ? 700 : 600}
      bodyStyle={{ padding: 0 }}
      className="sync-modal-override"
      centered
      maskClosable={viewState !== 'progressing'}
      closable={viewState !== 'progressing'}
    >
      {viewState === 'initial' && renderInitial()}
      {viewState === 'progressing' && renderProgress()}
      {viewState === 'summary' && renderSummary()}
      {viewState === 'error' && (
          <Result
            status="error"
            title="Sync Failed"
            subTitle={progressState.error || "An error occurred while syncing."}
          />
      )}
    </Modal>
  );
};

export default SyncBrokerModal;
